import { useAtom } from 'jotai';
import { useEffect, useRef, useState } from 'react';
import { DEFAULT_MAP_ZOOM, type MapCenter, type MapViewAtoms } from '../atoms';

const MIN_MAP_ZOOM = 1;
const MAX_MAP_ZOOM = 18;
const PERSIST_MAP_DELAY = 500;

// Stored values can be hand-edited or left over from an older build, so neither is trusted on the way in.
const toValidZoom = (zoom: number) => (Number.isFinite(zoom) ? Math.min(Math.max(zoom, MIN_MAP_ZOOM), MAX_MAP_ZOOM) : DEFAULT_MAP_ZOOM);
const isValidCenter = (center: MapCenter | null): center is MapCenter => Array.isArray(center) && center.length === 2 && center.every((value) => Number.isFinite(value));

export const useMapView = ({ centerAtom, zoomAtom }: MapViewAtoms, fallbackCenter: MapCenter) => {
    const [storedCenter, setStoredCenter] = useAtom(centerAtom);
    const [storedZoom, setStoredZoom] = useAtom(zoomAtom);
    const [center, setCenter] = useState<MapCenter>(() => (isValidCenter(storedCenter) ? storedCenter : fallbackCenter));
    const [zoom, setZoom] = useState(() => toValidZoom(storedZoom));
    const latestView = useRef({ center, zoom });

    latestView.current = { center, zoom };

    // Bounds change on every pan and zoom frame, so settle first and write once the map stops moving.
    useEffect(() => {
        const timer = setTimeout(() => {
            setStoredCenter(center);
            setStoredZoom(zoom);
        }, PERSIST_MAP_DELAY);

        return () => clearTimeout(timer);
    }, [center, zoom]);

    // Toggling the view unmounts the map, which drops a pending write, so flush the last position on the way out.
    useEffect(
        () => () => {
            setStoredCenter(latestView.current.center);
            setStoredZoom(latestView.current.zoom);
        },
        [],
    );

    const handleBoundsChanged = ({ center, zoom }: { center: MapCenter; zoom: number }) => {
        setCenter(center);
        setZoom(zoom);
    };

    return { center, zoom, handleBoundsChanged };
};
